import { useMemo, useState } from 'react'
import { elementTypeToLabelSingular } from '@domainConstants'
import { BugReport, Launch } from '@mui/icons-material'
import { Box, Stack, Typography } from '@mui/material'
import {
  DataGridPremium,
  GridColDef,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-premium'
import { getCenterMultiple, ImmutableVector3 } from '@modugen/ui-toolkit'
import { ToggleButton, ToggleButtonGroup } from '@ui/actions'
import { TooltipIconButton } from '@ui/feedback'
import { DraggablePopup, HeaderBadge } from '@ui/structure'
import { useElementLoadStore, useTransmissionStore } from '@editorStores'
import {
  useElementLabel,
  useElementTypes,
  useGuidToDomain,
  useGuidToElementIncludingRips,
  useLoadProblems,
  usePositionGuidToWallGuid,
  useSceneMarker,
} from '@editorHooks'
import { useStructuralPlanningSelection } from '@structuralPlanningHooks'

// Maximum character length for each bullet point item
const MAX_BULLET_POINT_LENGTH = 95

const CustomToolbar = () => {
  return (
    <GridToolbarContainer>
      <GridToolbarQuickFilter sx={{ flex: 1, mb: 1 }} />
    </GridToolbarContainer>
  )
}

const ColoringControls = () => {
  const verticalTransmissionGraph = useTransmissionStore(state => state.verticalTransmissionGraph)
  const loadsPerElement = useElementLoadStore(state => state.loads)
  const elementTypes = useElementTypes()
  const positionGuidToWallGuid = usePositionGuidToWallGuid()
  const guidToElement = useGuidToElementIncludingRips()

  const guidToDomain = useGuidToDomain()

  const allProblems = useLoadProblems(
    verticalTransmissionGraph,
    elementTypes,
    positionGuidToWallGuid,
    guidToDomain,
    loadsPerElement,
    guidToElement as Record<string, ShapeObject>,
  )?.problemsOnElements

  const errorCount = useMemo(() => {
    return allProblems?.filter(problem => problem.severity === 'error').length ?? 0
  }, [allProblems])

  const [problemViewAnchorEl, setProblemViewAnchorEl] = useState<HTMLElement | null>(null)

  const getLabel = useElementLabel()

  const {
    actions: { setElements, toggleMode },
  } = useStructuralPlanningSelection()

  const columns: GridColDef[] = [
    {
      field: 'actions',
      headerName: '',
      width: 30,
      minWidth: 30,
      sortable: false,
      display: 'flex',
      align: 'center',
      filterable: false,
      disableColumnMenu: true,
      renderCell: params => (
        <TooltipIconButton
          tooltip="Element anzeigen (erfordert Tragwerksplanungsmodus)"
          onClick={e => {
            e.stopPropagation()
            toggleMode(undefined)
            setElements([params.row.element_guid])
          }}
        >
          <Launch />
        </TooltipIconButton>
      ),
    },
    {
      field: 'label',
      headerName: 'Bauteil',
      width: 80,
      sortable: true,
      valueGetter: (value, row: { element_guid: string }) => getLabel(row.element_guid),
      renderCell: params => <Typography>{params.value}</Typography>,
      align: 'left',
      display: 'flex',
    },
    {
      field: 'element_type',
      headerName: 'Typ',
      sortable: true,
      valueGetter: (value, row: { element_guid: string }) => {
        const type = elementTypes[row.element_guid]
        return type ? elementTypeToLabelSingular[type] : ''
      },
      renderCell: params => <Typography variant="body2">{params.value}</Typography>,
      align: 'left',
      display: 'flex',
    },
    {
      field: 'element_guid',
      headerName: 'Element GUID',
      sortable: true,
    },
    {
      field: 'severity',
      headerName: 'Art',
      width: 75,
      sortable: true,
      renderCell: params => (
        <Typography
          variant="body2"
          color={params.value === 'error' ? 'error' : 'warning'}
          alignSelf="center"
        >
          {params.value === 'error' ? 'Fehler' : 'Warnung'}
        </Typography>
      ),
      align: 'left',
      display: 'flex',
    },
    {
      field: 'message',
      headerName: 'Probleme',
      flex: 1,
      display: 'flex',
      sortable: false,
      renderCell: params => {
        const sentences = params.value.split('. ').filter((s: string) => s.trim())
        return (
          <Stack direction="column">
            {sentences.map((sentence: string, index: number) => {
              const trimmedSentence = sentence.trim()
              const truncatedSentence =
                trimmedSentence.length > MAX_BULLET_POINT_LENGTH
                  ? trimmedSentence.substring(0, MAX_BULLET_POINT_LENGTH) + '...'
                  : trimmedSentence
              return (
                <Typography key={index} fontSize={12}>
                  • {truncatedSentence}
                </Typography>
              )
            })}
          </Stack>
        )
      },
    },
  ]

  const getRowId = (row: {
    element_guid: string
    message: string
    severity: 'error' | 'warning'
  }) => `${row.element_guid}-${row.severity}-${row.message}`

  const [selectedRow, setSelectedRow] = useState<string | null>(null)

  const selectedProblem = useMemo(() => {
    if (selectedRow && allProblems) {
      return allProblems.find(problem => getRowId(problem) === selectedRow)
    }
    return null
  }, [selectedRow, allProblems])

  const selectedElement = useMemo(() => {
    if (selectedRow) {
      const element = selectedRow.split('-')[0]
      return guidToElement[element]
    }
    return null
  }, [selectedRow, guidToElement])

  const sceneMarker = useMemo(() => {
    if (selectedElement) {
      if ('start' in selectedElement) {
        const center = getCenterMultiple([selectedElement.start, selectedElement.end])
        return center
      }
      if ('shape' in selectedElement && 'points' in selectedElement.shape) {
        const shape = selectedElement.shape.points
        return getCenterMultiple(shape)
      }
    }

    return null
  }, [selectedElement])

  useSceneMarker({
    marker: sceneMarker as unknown as ImmutableVector3,
    enabled: !!sceneMarker && !!problemViewAnchorEl,
    disableDepthTest: true,
  })

  return (
    <>
      <ToggleButtonGroup>
        <HeaderBadge badgeContent={errorCount ?? 0} color="error">
          <ToggleButton
            hint="Probleme / Warnungen im Modell"
            onClick={event => setProblemViewAnchorEl(event.currentTarget)}
            selected={false}
            Icon={BugReport}
          />
        </HeaderBadge>
      </ToggleButtonGroup>

      <DraggablePopup
        anchorEl={problemViewAnchorEl || undefined}
        closePopover={() => setProblemViewAnchorEl(null)}
        title="Probleme / Warnungen im Modell"
        childrenBoxSx={{
          maxHeight: 600,
          width: 900,
          overflowY: 'auto',
        }}
      >
        <DataGridPremium
          rows={allProblems || []}
          columns={columns}
          getRowId={getRowId}
          pageSizeOptions={[5, 10, 25]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 25 },
            },
            columns: {
              columnVisibilityModel: {
                element_guid: false,
              },
            },
            filter: {
              filterModel: {
                items: [],
                quickFilterExcludeHiddenColumns: false,
              },
            },
          }}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          columnHeaderHeight={30}
          density="compact"
          onRowClick={params => setSelectedRow(params.id as string)}
          rowSelectionModel={selectedRow ? [selectedRow] : []}
          slots={{
            toolbar: CustomToolbar,
          }}
        />
        {selectedProblem ? (
          <Box>
            {selectedProblem.message.split('. ').map((sentence, index) => {
              const trimmedSentence = sentence.trim()
              if (trimmedSentence) {
                return (
                  <Typography key={index} variant="body2">
                    - {trimmedSentence}
                  </Typography>
                )
              }
              return null
            })}
          </Box>
        ) : (
          <></>
        )}
      </DraggablePopup>
    </>
  )
}

export default ColoringControls
